import {
  Agricultural,
  AgriculturalLineOfCredit,
  AgriculturalPurchaseAcreage,
  AgriculturalPurchaseEquipment,
  AgriculturalRefinance,
  BorrowerType,
  BrokerLoanOfficer,
  Collateral,
  CommercialBridgeLoan,
  CommercialBusiness,
  CommercialEquipment,
  CommercialLoan,
  CommercialManagement,
  CommercialMortgage,
  CommercialOwner,
  CommercialPurchase,
  CommercialRefinance,
  CommercialVehicle,
  DynamicOwner,
  EIDL,
  Ethnicity,
  EthnicityName,
  Employment,
  FormType,
  LiabilityAndExpenseType,
  LivingSituation,
  ResidenceDetailType,
  ReverseMortgageLoanType,
  Sex,
  SmallBusiness,
  UniversalLoanBorrower,
  UniversalLoanSource,
  UniversalLoanApplication,
  EthnicityHispanic,
  RaceAsian,
  Race,
  RaceIslander,
  UniversalSubjectProperty,
  UniversalResidence,
  UniversalReoProperty,
  ReferralCode,
} from '@lenderful/domain';
import {
  getLineOfCredit,
  getLoanTerm,
  getTenurePayment,
  getTermPayment,
} from 'app/api/loan-application/universal/reverse';
import { borrowerSsnRef, coBorrowerSsnRef } from 'app/components/FormFields/Ssn/CollectSsnBase';
import moment from 'moment';
import { ownerSsnRefs } from '../../../components/SmallBusinessCollectSsnStep';
import {
  DeclarationFieldName,
  arrayContains,
  hasBorrower,
  hasBusinessTypeOther,
  hasCoBorrower,
  hasEIDL,
  isNonProfit,
  isPrimaryHome,
  isRefinance,
  isUsingReverseToPurchase,
  isYes,
} from '../../../models/fields/conditionals';
import { FieldNames, FieldNames as Fields } from '../../../models/fields/names';
import {
  YesOrNo,
} from '../../../models/options/enums';
import { normalizePhone, toNormalizedSsn } from '../../../util/normalizers';
import {
  mapAssets,
  mapCitizenship,
  mapCoBorrowerAssets,
  mapCoBorrowerEmployment,
  mapCoBorrowerIncome,
  mapDeclarationExplanation,
  mapIncome,
  mapLiabilities,
  mapResidentAlien,
  safelyTrim,
  toBinaryFlag,
  toBinaryFlagOrUndefined,
  toSqlDate,
} from '../mappings';
import { getREOMonthlyExpenseAmount, getREOMonthlyRentAmount } from 'app/util/formulas';

export const isReverseMortgageType = (form, type: ReverseMortgageLoanType): boolean => {
  return form[FieldNames.reverseMortgageLoanType] === type;
};

const isReverseMortgage = (type: FormType) => type === FormType.REVERSE;
const isPersonalLoan = (type: FormType) => type === FormType.PERSONAL || type === FormType.PERSONAL_LOAN_INQUIRY;
const isAutoLoan = (type: FormType) => type === FormType.AUTO || type === FormType.AUTO_BASIC || type === FormType.AUTO_LOAN_INQUIRY;
const isOtherVehicleLoan = (type: FormType) => type === FormType.OTHER_VEHICLE || type === FormType.OTHER_VEHICLE_BASIC || type === FormType.OTHER_VEHICLE_INQUIRY;
const isCreditCard = (type: FormType) => type === FormType.CREDIT_CARD;
const isCreditBooster = (type: FormType) => type === FormType.CREDIT_BOOSTER;
const isSmallBusinessLoan = (type: FormType) => type === FormType.SMALL_BUSINESS_LINE_OF_CREDIT || type === FormType.SMALL_BUSINESS_TERM;
const isCommercialLoan = (type: FormType) => type.includes(FormType.COMMERCIAL);
const isAgriculturalLoan = (type: FormType) => type.includes(FormType.AGRICULTURAL);

/**
 * Maps the values in the existing form to the Universal Loan Application, which is
 * used for Consumer Loans, Credit Card, and Reverse Mortgage products.
 *
 * @param {any} form
 * @param {FormType} formName
 * @returns {UniversalLoanApplication}
 */
export const mapToUniversalLoanApplication = (form, formName: FormType, referralCodes: ReferralCode[] = []): UniversalLoanApplication => {

  const application: UniversalLoanApplication = {
    assets                  : form[Fields.assets],
    borrower                : mapUniversalBorrowers(form, formName) as UniversalLoanBorrower[],
    brokerLoanOfficer       : mapBrokerLoanOfficer(form),
    coborrowerOnLoanFlag    : toBinaryFlagOrUndefined(form[Fields.coBorrowerYN]),
    coborrowerOnTitleFlag   : toBinaryFlagOrUndefined(form[Fields.coBorrowerOnTitle]),
    creditReportId          : form[FieldNames.creditReportId],
    employment              : mapUniversalEmployment(form, formName),
    fee                     : form[Fields.fee],
    livingMonthlyRentAmount : form[Fields.livingMonthlyRentAmount],
    loanType                : form[Fields.loanType],
    monthlyPayment          : form[Fields.monthlyPayment],
    propertyZip             : form[Fields.propertyZip],
    realEstateLiabilities   : form[Fields.realEstateLiabilities],
    source                  : formName as UniversalLoanSource,
    subjectProperty         : mapUniversalSubjectProperty(form, formName),
    referralCodes,
  };

  // Reverse Mortgage
  if (isReverseMortgage(formName)) {
    application.reverseMortgage = {
      downPayment                : form[FieldNames.downDollar],
      expectedLumpSum            : form[FieldNames.expectedLumpSum],
      hasSelectedNewHome         : toBinaryFlagOrUndefined(form[FieldNames.hasSelectedNewHome]),
      hubSpotContactId           : form[FieldNames.hubSpotContactId],
      lineOfCredit               : getLineOfCredit(form),
      loanTerm                   : getLoanTerm(form),
      initialMortgageInsurance   : form[FieldNames.initialMortgageInsurance],
      maxClaimAmount             : form[FieldNames.maxClaimAmount],
      maxLumpSum                 : form[FieldNames.maxLumpSum],
      netPrincipalLimit          : form[FieldNames.netPrincipalLimit],
      reverseMortgageLoanType    : form[FieldNames.reverseMortgageLoanType],
      reverseMortgageLoanPurpose : form[FieldNames.reverseMortgageLoanPurpose],
      tenurePayment              : getTenurePayment(form),
      termPayment                : getTermPayment(form),
      totalUpfrontCosts          : form[FieldNames.totalUpfrontCosts],
      quantumLoanId              : form[FieldNames.quantumLoanId],
      quantumSimpleId            : form[FieldNames.quantumSimpleId],
      isQuantumFileStarted       : form[FieldNames.isQuantumFileStarted],
    };
    if(!isUsingReverseToPurchase(form)) {
      delete application.subjectProperty.purchase_amount;
    }
  }
  // Auto Loan
  if (isAutoLoan(formName)) {
    application.autoLoan = {
      autoPurchasePlan : form[Fields.autoPurchasePlan],
      loanTermDesired  : Number(form[Fields.loanTermDesired]),
      loanTerm         : form[Fields.loanTerm],
      rate             : form[Fields.rate],
      retailValue      : form[Fields.autoRetailValue],
      productType      : form[Fields.productType],
      purchasePrice    : form[Fields.purchasePrice],
      downPayment      : form[Fields.downDollar],
      tradeInValue     : form[Fields.tradeInValue],
      vehicle          : form[Fields.vehicle],
      vehicleId        : form[Fields.vehicleId],
      vehicleBody      : form[Fields.vehicleBody],
      vehicleMake      : form[Fields.vehicleMake],
      vehicleModel     : form[Fields.vehicleModel],
      vehicleYear      : form[Fields.vehicleYear],
      vehicleMileage   : form[Fields.vehicleMileage],
    };
  }
  // Other Vehicle Loan
  if (isOtherVehicleLoan(formName)) {
    application.otherVehicleLoan = {
      downPayment     : form[Fields.downDollar],
      loanTermDesired : Number(form[Fields.loanTermDesired]),
      purchasePrice   : form[Fields.purchasePrice],
      tradeInValue    : form[Fields.tradeInValue],
      vehicle         : form[Fields.vehicle],
      vehicleType     : form[Fields.vehicleType],
    };
  }
  // Credit Card
  if (isCreditCard(formName)) {
    application.creditCard = {
      creditCardLimit   : form[Fields.creditCardLimit],
      creditCardType    : form[Fields.creditCardType],
      mothersMaidenName : form[Fields.mothersMaidenName],
    };

    application.borrower
      .forEach(borrower => {
        if (borrower.RESIDENCES?.[0]) {
          borrower.RESIDENCES[0].current_residence_flag = undefined;
          if (borrower.borrowerType === BorrowerType.RELATIVE) {
            delete borrower.RESIDENCES[0].ownership_type;
          }
        }
      });
  }
  // Credit Booster Loan
  if (isCreditBooster(formName)) {
    application.creditBoosterLoan = {
      accountSummary          : form[Fields.accountSummary],
      creditBoosterLoanOption : form[Fields.creditBoosterLoanOption],
      loanAmount              : form[Fields.loanAmount],
    };
  }
  // Personal Loan
  if (isPersonalLoan(formName)) {
    application.personalLoan = {
      hasCurrentCheckingAccount : form[Fields.hasCurrentCheckingAccount],
      loanTerm                  : form[FieldNames.loanTerm],
      productType               : form[FieldNames.productType],
      rate                      : form[FieldNames.rate],
      requestedLoanAmount       : form[FieldNames.loanAmount],
    };
  }
  // Commercial Loans
  if (isCommercialLoan(formName)) {
    application.commercialLoan  = handleCommercialMapping(form, formName);
  }
  // Small Business
  if(isSmallBusinessLoan(formName)) {
    application.smallBusiness = mapSmallBusiness(form);
  }
  // Agricultural Loans
  if (isAgriculturalLoan(formName)) {
    application.agricultural  = handleAgriculturalMapping(form, formName);
  }
  return application;
};

const mapUniversalBorrowerResidences = (form, formName: FormType): UniversalResidence[] => {
  const residences = [mapUniversalBorrowerResidence(form, formName)];
  if (form[Fields.livingPrevPropertyStreet]) {
    residences.push(mapUniversalBorrowerPreviousResidence(form));
  }
  if (form[Fields.mailingStreet]) {
    residences.push(mapUniversalBorrowerMailingResidence(form));
  }
  return residences;
};

const mapUniversalBorrowerResidence = (form, formName: FormType): UniversalResidence => {
  // for refinance of primary home, residence is subject property
  if (isRefinance(form) && isPrimaryHome(form)) {
    const liabilitiesAndExpenses = [];
    if (form[Fields.yearInsure]) {
      liabilitiesAndExpenses.push({ amount: form[Fields.yearInsure], type: LiabilityAndExpenseType.HOI });
    }
    if (form[Fields.yearTaxes]) {
      liabilitiesAndExpenses.push({ amount: form[Fields.yearTaxes], type: LiabilityAndExpenseType.PROPERTY_TAXES });
    }
    if (form[Fields.yearAssociate]) {
      liabilitiesAndExpenses.push({ amount: form[Fields.yearAssociate], type: LiabilityAndExpenseType.HOA });
    }
    return {
      id                         : form[Fields.propertyId],
      city                       : form[Fields.propertyCity],
      current_residence_flag     : form[Fields.propertyStreet] ? toBinaryFlagOrUndefined(YesOrNo.YES) : undefined,
      duration_months            : handleDurationInMonths(form[Fields.livingOccupancyStart]),
      escro_flag                 : toBinaryFlagOrUndefined(form[Fields.escrowUsage]),
      liabilitiesAndExpenses,
      occupancyStartDate         : toSqlDate(form[Fields.livingOccupancyStart]),
      property_title_held        : form[Fields.propertyTitleHeld],
      property_type              : formName.includes(FormType.COMMERCIAL) ? form[Fields.livingPropertyType] : form[Fields.propertyType],
      property_use               : form[Fields.propertyUsage],
      postal_code                : form[Fields.propertyZip],
      residence_detail_type      : form[Fields.propertyStreet] ? ResidenceDetailType.CURRENT : undefined,
      ownership_type             : LivingSituation.OWN,
      state                      : form[Fields.propertyState],
      street_address_1           : form[Fields.propertyStreet],
      street_address_2           : form[Fields.propertyStreet2],
    };
  }
  const liabilitiesAndExpenses = [];
  if (form[Fields.livingYearInsure]) {
    liabilitiesAndExpenses.push({
      amount: form[Fields.livingYearInsure],
      id: form[Fields.livingYearInsureId],
      type: LiabilityAndExpenseType.HOI,
    });
  }
  if (form[Fields.livingYearTaxes]) {
    liabilitiesAndExpenses.push({
      amount: form[Fields.livingYearTaxes],
      id: form[Fields.livingYearTaxesId],
      type: LiabilityAndExpenseType.PROPERTY_TAXES,
    });
  }
  if (form[Fields.livingYearAssociate]) {
    liabilitiesAndExpenses.push({
      amount: form[Fields.livingYearAssociate],
      id: form[Fields.livingYearAssociateId],
      type: LiabilityAndExpenseType.HOA,
    });
  }
  return {
    id                         : form[Fields.livingPropertyId],
    city                       : form[Fields.livingPropertyCity],
    current_residence_flag     : form[Fields.livingPropertyStreet] ? toBinaryFlagOrUndefined(YesOrNo.YES) : undefined,
    duration_months            : handleDurationInMonths(form[Fields.livingOccupancyStart]),
    escro_flag                 : toBinaryFlagOrUndefined(form[Fields.livingEscrowUsage]),
    liabilitiesAndExpenses,
    monthly_amount             : form[Fields.livingMonthlyMortgage] || form[Fields.livingMonthlyRentAmount],
    occupancyStartDate         : toSqlDate(form[Fields.livingOccupancyStart]),
    postal_code                : form[Fields.livingPropertyZip],
    property_title_held        : form[Fields.propertyTitleHeld],
    property_type              : formName.includes(FormType.COMMERCIAL) || isReverseMortgage(formName) ? form[Fields.livingPropertyType] : form[Fields.propertyType],
    property_use               : isReverseMortgage(formName) ? form[Fields.livingPropertyUsage] : form[Fields.propertyUsage],
    residence_detail_type      : form[Fields.propertyStreet] ? ResidenceDetailType.CURRENT : undefined,
    ownership_type             : form[Fields.livingRentOrOwn],
    state                      : form[Fields.livingPropertyState],
    street_address_1           : form[Fields.livingPropertyStreet],
    street_address_2           : form[Fields.livingPropertyStreet2],
  };
};

const mapUniversalBorrowerPreviousResidence = (form): UniversalResidence => {
  return {
    city                     : form[Fields.livingPrevPropertyCity],
    current_residence_flag   : form[Fields.livingPrevPropertyStreet] ? toBinaryFlagOrUndefined(YesOrNo.NO) : undefined,
    duration_months          : moment(form[Fields.livingOccupancyStart]).diff(moment(form[Fields.livingPrevOccupancyStart]), 'months'),
    postal_code              : form[Fields.livingPrevPropertyZip],
    ownership_type           : form[Fields.livingPrevRentOrOwn],
    occupancyStartDate       : toSqlDate(form[Fields.livingPrevOccupancyStart]),
    residence_detail_type    : form[Fields.livingPrevPropertyStreet] ? ResidenceDetailType.PREVIOUS : undefined,
    state                    : form[Fields.livingPrevPropertyState],
    street_address_1         : form[Fields.livingPrevPropertyStreet],
    street_address_2         : form[Fields.livingPrevPropertyStreet2],
  };
};

const mapUniversalBorrowerMailingResidence = (form): UniversalResidence => {
  return {
    city                     : form[Fields.mailingCity],
    current_residence_flag   : form[Fields.mailingStreet] ? toBinaryFlagOrUndefined(YesOrNo.NO) : undefined,
    postal_code              : form[Fields.mailingZip],
    ownership_type           : LivingSituation.OWN,
    residence_detail_type    : form[Fields.mailingStreet] ? ResidenceDetailType.MAILING : undefined,
    state                    : form[Fields.mailingState],
    street_address_1         : form[Fields.mailingStreet],
    street_address_2         : form[Fields.mailingStreet2],
  };
};

const mapUniversalBorrowers = (form, formName: FormType): Partial<UniversalLoanBorrower>[] => {
  const borrowers: Partial<UniversalLoanBorrower>[]     = [];
  const primaryType = FieldNames.decBorrower;
  if (hasBorrower(form)) {
    let primaryBorrower: Partial<UniversalLoanBorrower> = {
      id                              : form[Fields.borrowerId],
      birth_date                      : form[Fields.dateOfBirth] ? toSqlDate(form[Fields.dateOfBirth]) : undefined,
      credit_score                    : form[Fields.creditScore],
      CURRENT_ASSET                   : mapAssets(form, formName),
      CURRENT_INCOME                  : mapIncome(form, formName),
      CURRENT_LIABILITY               : mapLiabilities(form),
      RESIDENCES                      : mapUniversalBorrowerResidences(form, formName),
      REO_PROPERTY                    : mapReoProperty(form),
      credit_report_reference         : form[Fields.creditReportId],
      email                           : form[Fields.email],
      EMPLOYMENT                      : mapUniversalEmployment(form, formName),
      first_name                      : safelyTrim(form[Fields.firstName]),
      income_on_decline_flag          : toBinaryFlagOrUndefined(form[Fields.incomeOnDeclineYN]),
      last_name                       : safelyTrim(form[Fields.lastName]),
      marital_status                  : form[Fields.maritalStatus],
      middle_name                     : safelyTrim(form[Fields.middleName]),
      name_suffix                     : form[Fields.suffixName],
      phone                           : form[Fields.phone],
      primary_borrower_flag           : toBinaryFlagOrUndefined(true),
      DECLARATION_EXPLANATION         : mapDeclarationExplanation(form, primaryType),
      ssn                             : borrowerSsnRef ? toNormalizedSsn(borrowerSsnRef.value) : undefined,
      borrowerType                    : BorrowerType.PRIMARY,
      dec_citizenship_flag            : mapCitizenship(form, primaryType),
      dec_resident_alien_flag         : mapResidentAlien(form, primaryType),
    };
    if(isReverseMortgage(formName)) {
      primaryBorrower = {
        ...primaryBorrower,
        age                             : form[Fields.age],
        dec_bankruptcy_flag             : toBinaryFlagOrUndefined(form[primaryType][Fields.hasBankrupt]),
        dec_comaker_endorser_flag       : toBinaryFlagOrUndefined(form[primaryType][Fields.hasNoteEndorsement]),
        dec_home_owner_occupy_flag      : toBinaryFlagOrUndefined(form[primaryType][Fields.hasOccupyPrimary]),
        dec_outstanding_judgements_flag : toBinaryFlagOrUndefined(form[primaryType][Fields.hasJudgements]),
        dec_party_to_lawsuit_flag       : toBinaryFlagOrUndefined(form[primaryType][Fields.hasLawsuit]),
        dec_presently_delinquent_flag   : toBinaryFlagOrUndefined(form[primaryType][Fields.hasDelinquent]),
        sex_female_flag                 : toBinaryFlagOrUndefined(arrayContains(form[primaryType][Fields.sex], Sex.FEMALE)),
        sex_male_flag                   : toBinaryFlagOrUndefined(arrayContains(form[primaryType][Fields.sex], Sex.MALE)),
        sex_not_provided_flag           : toBinaryFlagOrUndefined(arrayContains(form[primaryType][Fields.sex], Sex.NOSHARE)),
        ...mapRace(form, primaryType),
        ...mapEthnicity(form, primaryType),
      };
    }
    if(formName.includes(FormType.COMMERCIAL || FormType.AGRICULTURAL)  ) {
      primaryBorrower = {
        ...primaryBorrower,
        dec_commercial_bankrupt_flag         : toBinaryFlagOrUndefined(form[primaryType][Fields.hasCommercialBankrupt]),
        dec_commercial_credit_flag           : toBinaryFlagOrUndefined(form[primaryType][Fields.hasCommercialCredit]),
        dec_commercial_debt_flag             : toBinaryFlagOrUndefined(form[primaryType][Fields.hasCommercialDebt]),
        dec_commercial_legal_action_flag     : toBinaryFlagOrUndefined(form[primaryType][Fields.hasCommercialLegalAction]),
        dec_commercial_tax_due_flag          : toBinaryFlagOrUndefined(form[primaryType][Fields.hasCommercialTaxDue]),
      };
    }
    borrowers.push(primaryBorrower);
  }

  if (hasCoBorrower(form)) {
    const coType = FieldNames.decCoBorrower;
    let coBorrower: Partial<UniversalLoanBorrower> | any = {
      id                              : form[Fields.coBorrowerId],
      birth_date                      : form[Fields.coBorrowerDOB] ? toSqlDate(form[Fields.coBorrowerDOB]) : undefined,
      first_name                      : safelyTrim(form[Fields.coBorrowerFirstName]),
      last_name                       : safelyTrim(form[Fields.coBorrowerLastName]),
      middle_name                     : safelyTrim(form[Fields.coBorrowerMiddleName]),
      name_suffix                     : form[Fields.coBorrowerSuffixName],
      email                           : form[Fields.coBorrowerEmail],
      CURRENT_ASSET                   : mapCoBorrowerAssets(form, formName),
      CURRENT_INCOME                  : mapCoBorrowerIncome(form, formName),
      EMPLOYMENT                      : mapCoBorrowerEmployment(form),
      RESIDENCES                      : [mapUniversalCoBorrowerResidence(form, formName)],
      primary_borrower_flag           : toBinaryFlagOrUndefined(false),
      credit_report_reference         : form[Fields.creditReportId],
      income_on_decline_flag          : toBinaryFlagOrUndefined(form[Fields.coBorrowerIncomeOnDeclineYN]),
      phone                           : form[Fields.coBorrowerPhone],
      DECLARATION_EXPLANATION         : mapDeclarationExplanation(form, coType),
      ssn                             : coBorrowerSsnRef ? toNormalizedSsn(coBorrowerSsnRef.value) : undefined,
      borrowerType                    : BorrowerType.COBORROWER,
      marital_status                  : form[Fields.coBorrowerMaritalStatus],
      dec_citizenship_flag            : mapCitizenship(form, coType),
      dec_resident_alien_flag         : mapResidentAlien(form, coType),
      coborrower_on_title_flag        : toBinaryFlagOrUndefined(form[Fields.coBorrowerOnTitle]),
    };
    if(isReverseMortgage(formName)) {
      coBorrower = {
        ...coBorrower,
        age                             : form[Fields.coBorrowerAge],
        dec_bankruptcy_flag             : toBinaryFlagOrUndefined(form[primaryType][Fields.hasBankrupt]),
        dec_comaker_endorser_flag       : toBinaryFlagOrUndefined(form[primaryType][Fields.hasNoteEndorsement]),
        dec_home_owner_occupy_flag      : toBinaryFlagOrUndefined(form[primaryType][Fields.hasOccupyPrimary]),
        dec_outstanding_judgements_flag : toBinaryFlagOrUndefined(form[primaryType][Fields.hasJudgements]),
        dec_party_to_lawsuit_flag       : toBinaryFlagOrUndefined(form[primaryType][Fields.hasLawsuit]),
        dec_presently_delinquent_flag   : toBinaryFlagOrUndefined(form[primaryType][Fields.hasDelinquent]),
        sex_female_flag: toBinaryFlagOrUndefined(arrayContains(form[coType][Fields.sex], Sex.FEMALE)),
        sex_male_flag: toBinaryFlagOrUndefined(arrayContains(form[coType][Fields.sex], Sex.MALE)),
        sex_not_provided_flag: toBinaryFlagOrUndefined(arrayContains(form[coType][Fields.sex], Sex.NOSHARE)),
        ...mapRace(form, coType),
        ...mapEthnicity(form, coType),
      };
    }
    borrowers.push(coBorrower);
  }
  if (formName === FormType.CREDIT_CARD) {
    const coType = FieldNames.decRelative;
    const relative: Partial<UniversalLoanBorrower> | any = {
      birth_date                      : toSqlDate(form[Fields.relativeDOB]),
      first_name                      : safelyTrim(form[Fields.relativeFirstName]),
      last_name                       : safelyTrim(form[Fields.relativeLastName]),
      middle_name                     : safelyTrim(form[Fields.relativeMiddleName]),
      name_suffix                     : form[Fields.relativeSuffixName],
      email                           : form[Fields.relativeEmail],
      phone                           : form[Fields.relativePhone],
      primary_borrower_flag           : toBinaryFlagOrUndefined(false),
      DECLARATION_EXPLANATION         : mapDeclarationExplanation(form, coType),
      RESIDENCES                      : [mapRelativeResidence(form, formName)],
      borrowerType                   : BorrowerType.RELATIVE,
    };
    borrowers.push(relative);
  }
  return borrowers;
};

const mapRelativeResidence = (form, formName: FormType): UniversalResidence => {
  const borrowerResidence: UniversalResidence = mapUniversalBorrowerResidence(form, formName);

  // these fields don't apply for a relative living at same address as borrower
  const residenceFieldsThatDoNotApply = [
    'annual_insurance_amount',
    'annual_tax_amount',
    'association_annual_amount',
    'monthly_amount',
  ];
  residenceFieldsThatDoNotApply.forEach(field => {
    delete borrowerResidence[field];
  });

  const relativeResidence: UniversalResidence = {
    city                     : form[Fields.relativeLivingPropertyCity],
    current_residence_flag   : form[Fields.relativeLivingPropertyStreet] ? toBinaryFlagOrUndefined(YesOrNo.YES) : undefined,
    postal_code              : form[Fields.relativeLivingPropertyZip],
    ownership_type           : LivingSituation.NORENT, // Added this so UniversalResidence wouldn't be partial,
    residence_detail_type     : form[Fields.relativeLivingPropertyStreet] ? ResidenceDetailType.CURRENT : undefined,
    state                    : form[Fields.relativeLivingPropertyState],
    street_address_1         : form[Fields.relativeLivingPropertyStreet],
    street_address_2         : form[Fields.relativeLivingPropertyStreet2],
  };
  return form[Fields.relativeSameAddress] === YesOrNo.YES ? borrowerResidence : relativeResidence;
};

const mapUniversalEmployment = (form, formName: FormType): Employment[] => {
  const employment: Employment[] = [];

  if (form[FieldNames.employCompanyName] || (form[FieldNames.commercialBusinessName] && form[FieldNames.employmentType])) {
    employment.push({
      city                   : form[Fields.employCity],
      country                : 'US',
      current_employeer_flag : toBinaryFlagOrUndefined(true),
      employer_name          : form[Fields.employCompanyName],
      employment_type        : form[Fields.employmentType],
      job_start_date         : toSqlDate(form[Fields.employStart]),
      ownership_share        : form[Fields.employOwnershipShare],
      party_to_transaction   : toBinaryFlagOrUndefined(form[Fields.employPartyTransaction]),
      phone                  : form[Fields.employPhone],
      postal_code            : form[Fields.employZip],
      job_title              : form[Fields.employTitle],
      state                  : form[Fields.employState],
      street_address_1       : form[Fields.employStreet],
      street_address_2       : form[Fields.employStreet2],
    });
  }

  if(formName.includes(FormType.COMMERCIAL)) {
    if(form[Fields.commercialEmploySameBusiness] === YesOrNo.YES) {
      // If the applicant says they work for the business requesting the loan, then we never ask the above employ___ questions
      let primaryEmployment = employment.pop();
      primaryEmployment.city             = form[Fields.propertyCity];
      primaryEmployment.employer_name    = form[Fields.commercialBusinessName];
      primaryEmployment.phone            = form[Fields.commercialPhone];
      primaryEmployment.postal_code      = form[Fields.commercialZip];
      primaryEmployment.state            = form[Fields.commercialState];
      primaryEmployment.street_address_1 = form[Fields.commercialStreet];
      primaryEmployment.street_address_2 = form[Fields.commercialStreet2];
      employment.push(primaryEmployment);
    }
  }

  if (form[FieldNames.employPrevName]) {
    employment.push({
      city                   : form[Fields.employPrevCity],
      country                : 'US',
      current_employeer_flag : toBinaryFlagOrUndefined(false),
      employer_name          : form[Fields.employPrevName],
      employment_type        : form[Fields.employmentType],
      job_end_date           : toSqlDate(form[Fields.employPrevEnd]),
      job_start_date         : toSqlDate(form[Fields.employPrevStart]),
      job_title              : form[Fields.employPrevTitle],
      postal_code            : form[Fields.employPrevZip],
      state                  : form[Fields.employPrevState],
      street_address_1       : form[Fields.employPrevStreet],
      street_address_2       : form[Fields.employPrevStreet2],
    });
  }
  return employment;
};

export const mapReoProperty = (form): UniversalReoProperty[] => {
  const addlProperties: UniversalReoProperty[] = [];
  if (Array.isArray(form[Fields.addlProperties])) {
    form[Fields.addlProperties].forEach((property) => {
      const liabilitiesAndExpenses = [];
      if (form[Fields.propertyAnnualInsuranceAmount]) {
        liabilitiesAndExpenses.push({ amount: form[Fields.propertyAnnualInsuranceAmount], type: LiabilityAndExpenseType.HOI });
      }
      if (form[Fields.propertyAnnaulTaxAmount]) {
        liabilitiesAndExpenses.push({ amount: form[Fields.propertyAnnaulTaxAmount], type: LiabilityAndExpenseType.PROPERTY_TAXES });
      }
      if (form[Fields.propertyAnnualAssociationAmount]) {
        liabilitiesAndExpenses.push({ amount: form[Fields.propertyAnnualAssociationAmount], type: LiabilityAndExpenseType.HOA });
      }
      if (form[Fields.mortgageBalance]) {
        liabilitiesAndExpenses.push({ amount: form[Fields.mortgageBalance], type: LiabilityAndExpenseType.MORTGAGE });
      }
      addlProperties.push({
        property_type               : property[Fields.propertyType],
        property_use                : property[Fields.propertyUsage],
        street_address              : property[FieldNames.propertyStreet],
        street_address_2            : property[FieldNames.propertyStreet2],
        city                        : property[FieldNames.propertyCity],
        state                       : property[FieldNames.propertyState],
        postal_code                 : property[FieldNames.propertyZip],
        present_value               : property[FieldNames.propertyValue],
        monthly_expense_amount      : getREOMonthlyExpenseAmount(property),
        monthly_gross_rental_income : getREOMonthlyRentAmount(property),
        liabilitiesAndExpenses,
      });
    });
  }
  return addlProperties;
};

export const mapEthnicity = (form, borrowerType: DeclarationFieldName): Partial<UniversalLoanBorrower> => {
  const hasEthnicity                 = (value) => toBinaryFlagOrUndefined(arrayContains(form[borrowerType][Fields.ethnicity], value));
  const hasHispanicEthnicity         = (value) => toBinaryFlag(arrayContains(form[borrowerType][Fields.ethnicityHispanic], value));
  return {
    ethnicity_hispanic_cuban_flag       : hasHispanicEthnicity(EthnicityHispanic.CUBAN),
    ethnicity_hispanic_flag             : hasEthnicity(Ethnicity.HISPANIC),
    ethnicity_hispanic_mexican_flag     : hasHispanicEthnicity(EthnicityHispanic.MEXICAN),
    ethnicity_hispanic_other            : form[borrowerType][Fields.ethnicityOther],
    ethnicity_hispanic_other_flag       : hasHispanicEthnicity(EthnicityHispanic.OTHER),
    ethnicity_hispanic_puerto_rican_flag: hasHispanicEthnicity(EthnicityHispanic.PUERTO),
    ethnicity_not_hispanic_flag         : hasEthnicity(Ethnicity.NOHISPANIC),
    ethnicity_not_provided_flag         : hasEthnicity(Ethnicity.NOSHARE),
  };
};

export const mapRace = (form, borrowerType: DeclarationFieldName): Partial<UniversalLoanBorrower> => {
  const hasRace         = (value) => toBinaryFlag(arrayContains(form[borrowerType][Fields.race], value));
  const hasAsianRace    = (value) => toBinaryFlag(arrayContains(form[borrowerType][Fields.raceAsian], value));
  const hasIslanderRace = (value) => toBinaryFlag(arrayContains(form[borrowerType][Fields.raceIslander], value));
  return {
    race_asian_chinese_flag           : hasAsianRace(RaceAsian.CHINESE),
    race_asian_filipino_flag          : hasAsianRace(RaceAsian.FILIPINO),
    race_asian_flag                   : hasRace(Race.ASIAN),
    race_asian_indian_flag            : hasAsianRace(RaceAsian.INDIAN),
    race_asian_japanese_flag          : hasAsianRace(RaceAsian.JAPANESE),
    race_asian_korean_flag            : hasAsianRace(RaceAsian.KOREAN),
    race_asian_other                  : form[borrowerType][Fields.raceAsianOther],
    race_asian_other_flag             : hasAsianRace(RaceAsian.OTHER),
    race_asian_vietnamese_flag        : hasAsianRace(RaceAsian.VIET),
    race_black_flag                   : hasRace(Race.AFRICAN),
    race_islander_flag                : hasRace(Race.ISLANDER),
    race_islander_guamanian_flag      : hasIslanderRace(RaceIslander.GUAM),
    race_islander_native_hawaiian_flag: hasIslanderRace(RaceIslander.NATIVE),
    race_islander_other               : form[borrowerType][Fields.raceIslanderOther],
    race_islander_other_flag          : hasIslanderRace(RaceIslander.OTHER),
    race_islander_samoan_flag         : hasIslanderRace(RaceIslander.SAMOAN),
    race_native_flag                  : hasRace(Race.NATIVE),
    race_not_provided_flag            : hasRace(Race.NOSHARE),
    race_white_flag                   : hasRace(Race.WHITE),
  };
};

export const mapUniversalCoBorrowerResidence = (form, formName: FormType): UniversalResidence => {
  const borrowerResidence: UniversalResidence = mapUniversalBorrowerResidence(form, formName);

  // these fields don't apply for a co-borrower living at same address as borrower
  const residenceFieldsThatDoNotApply = [
    'annual_insurance_amount',
    'annual_tax_amount',
    'association_annual_amount',
    'monthly_amount',
  ];
  residenceFieldsThatDoNotApply.forEach(field => {
    delete borrowerResidence[field];
  });

  const coBorrowerResidence: UniversalResidence = {
    city                     : form[Fields.coBorrowerLivingPropertyCity],
    current_residence_flag   : form[Fields.coBorrowerLivingPropertyStreet] ? toBinaryFlagOrUndefined(YesOrNo.YES) : undefined,
    monthly_amount           : form[Fields.coBorrowerLivingMonthlyRentAmount],
    postal_code              : form[Fields.coBorrowerLivingPropertyZip],
    ownership_type           : form[Fields.coBorrowerLivingRentOrOwn],
    residence_detail_type    : form[Fields.coBorrowerLivingPropertyStreet] ? ResidenceDetailType.CURRENT : undefined,
    state                    : form[Fields.coBorrowerLivingPropertyState],
    street_address_1         : form[Fields.coBorrowerLivingPropertyStreet],
    street_address_2         : form[Fields.coBorrowerLivingPropertyStreet2],
  };
  return form[Fields.coBorrowerSameAddress] === YesOrNo.YES ? borrowerResidence : coBorrowerResidence;
};

export const mapUniversalSubjectProperty = (form, formName: FormType): UniversalSubjectProperty => {
  const isOnTime = toBinaryFlagOrUndefined(form[FieldNames.hasLatePropertyCharges]) === 0;
  const liabilitiesAndExpenses = [];
  if (form[Fields.yearInsure]) {
    liabilitiesAndExpenses.push({ amount: form[Fields.yearInsure], type: LiabilityAndExpenseType.HOI });
  }
  if (form[Fields.yearTaxes]) {
    liabilitiesAndExpenses.push({ amount: form[Fields.yearTaxes], type: LiabilityAndExpenseType.PROPERTY_TAXES });
  }
  if (form[Fields.yearAssociate]) {
    liabilitiesAndExpenses.push({ amount: form[Fields.yearAssociate], type: LiabilityAndExpenseType.HOA });
  }
  if (form[Fields.mortgageBalance]) {
    liabilitiesAndExpenses.push({
      amount: form[Fields.mortgageBalance],
      id: form[Fields.mortgageBalanceId],
      type: LiabilityAndExpenseType.MORTGAGE,
    });
  }
  const subjectProperty: UniversalSubjectProperty = {
    built_year                          : form[Fields.builtYear],
    city                                : form[Fields.propertyCity],
    construction_cost                   : form[Fields.constructionCost],
    construction_land_acquired          : form[Fields.constructionLandAcquired],
    construction_land_owner_flag        : toBinaryFlagOrUndefined(form[Fields.constructionLandOwner]),
    escro_flag                          : toBinaryFlagOrUndefined(form[Fields.escrowUsage]),
    estimated_amount                    : isUsingReverseToPurchase(form) ? form[Fields.purchasePrice] : form[Fields.homeValue],
    is_annual_insurance_amount_ontime   : isOnTime ? 1 : undefined, // either on time or we're unsure (undefined)
    is_annual_tax_amount_ontime         : isOnTime ? 1 : undefined, // either on time or we're unsure (undefined)
    is_association_annual_amount_ontime : isOnTime ? 1 : undefined, // either on time or we're unsure (undefined)
    liabilitiesAndExpenses,
    monthly_gross_rental_income         : form[Fields.propertyMonthlyRentalIncome],
    original_cost                       : form[Fields.propertyOriginalCost],
    postal_code                         : form[Fields.propertyZip],
    property_acquired_year              : form[Fields.acquiredYear],
    property_type                       : form[Fields.propertyType],
    property_type_desc                  : form[Fields.propertyTypeDesc],
    property_use                        : form[Fields.propertyUsage],
    purchase_amount                     : isUsingReverseToPurchase(form) ? form[Fields.purchasePrice] : form[Fields.homeValue],
    state                               : form[Fields.propertyState],
    street_address_1                    : form[Fields.propertyStreet],
    street_address_2                    : form[Fields.propertyStreet2],
  };
  if(formName === FormType.COMMERCIAL_PURCHASE || formName === FormType.COMMERCIAL_REFINANCE) {
    subjectProperty.property_type    = form[Fields.commercialPropertyType];
    subjectProperty.estimated_amount = formName === FormType.COMMERCIAL_PURCHASE ? form[Fields.homeValue] : form[Fields.propertyValue];
  }
  return subjectProperty;
};

const handleDurationInMonths = (value: number): number | undefined => {
  if(value) {
    return moment().diff(moment(value), 'months');
  }
  return undefined;
};

export const handleCommercialMapping = (form: any, formName: FormType): CommercialLoan => {
  const isExistingLoan = form[FieldNames.existingLoanNumber]?.length > 0 || isYes(form[FieldNames.isCommercialExistingCustomer]);
  return {
    bridgeLoan         : mapCommercialBridgeLoan(form, formName),
    business           : mapCommercialBusiness(form),
    employSameBusiness : form[Fields.commercialEmploySameBusiness],
    equipment          : mapCommercialEquipment(form, formName),
    existingLoanId     : form[FieldNames.existingLoanNumber],
    hasOtherManagement : form[Fields.hasOtherManagement],
    loanHolder         : form[Fields.commercialLoanHolder],
    loanRenewalDate    :  moment(form[Fields.loanRenewalDate]).format('YYYY-MM-DD'),
    managers           : isExistingLoan ? undefined : mapCommercialManagement(form),
    owners             : isExistingLoan ? undefined : mapCommercialOwners(form),
    purchase           : mapCommercialPurchase(form, formName),
    refinance          : mapCommercialRefinance(form, formName),
    vehicles           : mapCommercialVehicles(form, formName),
  };
};

const mapCommercialBusiness = (form: any): CommercialBusiness => {
  return {
    businessName         : form[Fields.commercialBusinessName],
    dBA                  : form[Fields.commercialDBA],
    censusTract          : form[Fields.commercialCensusTract],
    naicsCode            : form[Fields.commercialNAICSCode],
    city                 : form[Fields.commercialSameBusiness] ? form[Fields.propertyCity]    : form[Fields.commercialCity],
    street               : form[Fields.commercialSameBusiness] ? form[Fields.propertyStreet]  : form[Fields.commercialStreet],
    street2              : form[Fields.commercialSameBusiness] ? form[Fields.propertyStreet2] : form[Fields.commercialStreet2],
    state                : form[Fields.commercialSameBusiness] ? form[Fields.propertyState]   : form[Fields.commercialState],
    zip                  : form[Fields.commercialSameBusiness] ? form[Fields.propertyZip]     : form[Fields.commercialZip],
    phone                : form[Fields.commercialPhone],
    employeeCount        : form[Fields.commercialEmployeeCount],
    website              : form[Fields.commercialWebsite],
    entityType           : form[Fields.commercialEntityType],
    businessType         : hasBusinessTypeOther(form) ? form[Fields.commercialBusinessTypeOther] : form[Fields.commercialBusinessType],
    stateOfIncorporation : form[Fields.commercialStateOfIncorporation],
    yearStarted          : form[Fields.commercialYearStarted],
    revenueTrend3        : form[Fields.commercialRevenueTrend3],
    revenueLastYear      : form[Fields.commercialRevenueLastYear],
    revenueExplanation   : form[Fields.commercialRevenueExplanation],
    profitTrend3         : form[Fields.commercialProfitTrend3],
    profitExplanation    : form[Fields.commercialProfitExplanation],
    collateralList       : form[Fields.commercialCollateralList],
    ownershipType        : form[Fields.commercialBusinessOwnershipType],
  };
};

const mapCommercialManagement = (form: any): CommercialManagement[] => {
  const managementList: CommercialManagement[] = [];
  form[Fields.commercialManagementList].forEach(element => {
    if(element[Fields.managementFirstName]) {
      managementList.push({
        firstName: element[Fields.managementFirstName],
        middleName: element[Fields.managementMiddleName],
        lastName: element[Fields.managementLastName],
        suffixName: element[Fields.managementSuffixName],
        titleHeld: element[Fields.managementTitleHeld],
      });
    }
  });
  return managementList;
};

const mapCommercialOwners = (form: any): CommercialOwner[] => {
  const owners: CommercialOwner[] = [];
  form[Fields.commercialOwnerList].forEach(element => {
    owners.push({
      firstName         : element[Fields.ownerFirstName],
      middleName        : element[Fields.ownerMiddleName],
      lastName          : element[Fields.ownerLastName],
      suffixName        : element[Fields.ownerSuffixName],
      titleHeld         : element[Fields.ownerTitleHeld],
      percentage        : element[Fields.ownerPercentage],
      guarantor         : element[Fields.ownerGuarantor],
      ethnicity         : element[FieldNames.ethnicity].map(value => {
        if (value === Ethnicity.HISPANIC) return EthnicityName.HISPANIC;
        else if(value === Ethnicity.NOHISPANIC) return EthnicityName.NOHISPANIC;
        return EthnicityName.NOSHARE;
      }),
      ethnicityHispanic : element[FieldNames.ethnicityHispanic],
      ethnicityOther    : element[FieldNames.ethnicityOther],
      race              : element[FieldNames.race],
      raceAsian         : element[FieldNames.raceAsian],
      raceAsianOther    : element[FieldNames.raceAsianOther],
      raceIslander      : element[FieldNames.raceIslander],
      raceIslanderOther : element[FieldNames.raceIslanderOther],
      sex        : element[Fields.sex],

    });
  });
  return owners;
};

const mapCommercialBridgeLoan = (form: any, formName: FormType): CommercialBridgeLoan => {
  if(formName === FormType.COMMERCIAL_BRIDGE_LOAN) {
    return {
      bridgeOrCredit : form[Fields.commercialBridgeOrCredit],
      loanAmount     : form[Fields.loanAmount],
      loanPurpose    : form[Fields.commercialLoanPurpose],
      loanTiming     : form[Fields.commercialLoanTiming],
    };
  }
  return undefined;
};

const mapCommercialEquipment = (form: any, formName: FormType): CommercialEquipment => {
  if(formName === FormType.COMMERCIAL_EQUIPMENT) {
    return {
      equipmentOrInventory : form[Fields.commercialEquipmentOrInventory],
      expectedCost         : form[Fields.commercialExpectedCost],
      downDollar           : form[Fields.downDollar],
      description          : form[Fields.commercialDescription],
      businessNeed         : form[Fields.commercialBusinessNeed],
      lifeExpectancy       : form[Fields.commercialLifeExpectancy],
      loanTiming           : form[Fields.commercialLoanTiming],
    };
  }
  return undefined;
};

const mapCommercialPurchase = (form: any, formName: FormType): CommercialPurchase => {
  if(formName === FormType.COMMERCIAL_PURCHASE) {
    return {
      hasChosenProperty: form[Fields.homeChosenYN],
      hasSignedAgreement: form[Fields.agreementYN],
      downDollar: form[Fields.downDollar],
      expectedClosingDate: form[Fields.expectedClosingDate],
      ...mapCommercialMortgage(form),
    };
  }
  return undefined;
};

const mapCommercialRefinance = (form: any, formName: FormType): CommercialRefinance => {
  if(formName === FormType.COMMERCIAL_REFINANCE) {
    return {
      lienBalance      : form[Fields.commercialLienBalance],
      loanAmount       : form[Fields.loanAmount],
      refinancePurpose : form[Fields.loanPurpose],
      ...mapCommercialMortgage(form),
    };
  }
  return undefined;
};

const mapCommercialVehicles = (form: any, formName: FormType): CommercialVehicle[] => {
  const result: CommercialVehicle[] = [];
  if(formName === FormType.COMMERCIAL_VEHICLE) {
    form[Fields.commercialVehicleList].forEach(element =>
      result.push({
        description        : element[Fields.vehicleType],
        year               : element[Fields.vehicleYear],
        make               : element[Fields.vehicleMake],
        model              : element[Fields.vehicleModel],
        count              : element[Fields.vehicleCount],
        newOrUsed          : element[Fields.vehicleNewOrUsed],
        totalPurchasePrice : element[Fields.commercialVehicleTotalPurchasePrice],
        totalDownPayment   : element[Fields.commercialVehicleTotalDownPayment],
        totalTradeInValue  : element[Fields.commercialVehicleTotalTradeInValue],
        loanTermDesired    : Number(element[Fields.loanTermDesired]),
      }),
    );
    return result;
  }
  return undefined;
};

const mapCommercialMortgage = (form: any): CommercialMortgage => {
  return {
    landSize                    : form[Fields.landSize],
    numOfStructures             : form[Fields.numOfStructures],
    buildingSquareFootage       : form[Fields.buildingSquareFootage],
    builtYear                   : form[Fields.builtYear],
    expectedOwnerOccupancy      : form[Fields.expectedOwnerOccupancy],
    currentZoningClassification : form[Fields.currentZoningClassification],
    buildingContactName         : form[Fields.buildingContactName],
    buildingContactPhone        : form[Fields.buildingContactPhone],
  };
};

const mapDynamicOwners = (form: any): DynamicOwner[] => {
  const result: DynamicOwner[] = [];
  if(!isNonProfit(form[FieldNames.commercialEntityType])) {
    form[Fields.commercialOwnerList].forEach((element, index) => {
      result.push({
        annualPersonalIncome : element[FieldNames.ownerAnnualPersonalIncome],
        city                 : element[FieldNames.ownerCity],
        isOwnerTheApplicant  : toBinaryFlagOrUndefined(element[FieldNames.ownerIsApplicant]),
        firstName            : element[FieldNames.ownerFirstName],
        email                : element[FieldNames.ownerEmail],
        lastName             : element[FieldNames.ownerLastName],
        middleName           : element[FieldNames.ownerMiddleName],
        ownershipPercentage  : element[FieldNames.ownerPercentage],
        ownerSince           : element[FieldNames.ownerSinceYear],
        personalNetWorth     : element[FieldNames.ownerPersonalNetWorth],
        phone                : element[FieldNames.ownerPhone],
        ssn                  : ownerSsnRefs?.[index]?.value ? toNormalizedSsn(ownerSsnRefs[index].value) : undefined,
        state                : element[FieldNames.ownerState],
        street               : element[FieldNames.ownerStreet],
        street2              : element[FieldNames.ownerStreet2],
        suffixName           : element[FieldNames.ownerSuffixName],
        zip                  : element[FieldNames.ownerZip],
      });
    });
  }
  return result;
};

const mapCollateral = (form: any): Collateral[] => {
  const result: Collateral[] = [];
  form[Fields.commercialCollateralList].forEach(element => {
    result.push({
      description : element[FieldNames.commercialCollateralDescription],
      value       : element[FieldNames.commercialCollateralValue],
    });
  });
  return result;
};

/**
 * This maps the Economic Injury Disaster Loan data to the appropriate data object
 *
 * @param form
 * @returns {EIDL[]}
 */
const mapEIDL = (form): EIDL[] => {
  if (hasEIDL(form)) {
    return form[FieldNames.economicInjuryDisasterLoans].map(loan => ({
      balanceOwed: loan.eidlBalance,
      bankName: loan.eidlBankName,
      collateral: loan.eidlCollateral,
      loanType: loan.eidlLoanType,
    }));
  }
  return [];
};

const mapSmallBusiness = (form: any): SmallBusiness => {
  return {
    annualInterestExpense       : form[FieldNames.commercialBusinessAnnualInterestExpense],
    businessCity                : form[FieldNames.commercialCity],
    businessDBA                 : form[FieldNames.commercialDBA],
    businessEntityType          : form[FieldNames.commercialEntityType],
    businessIsSameAsMailing     : toBinaryFlagOrUndefined(form[FieldNames.commercialSameMailingAddress]),
    businessMailingCity         : form[FieldNames.commercialMailingCity],
    businessMailingState        : form[FieldNames.commercialMailingState],
    businessMailingStreet       : form[FieldNames.commercialMailingStreet],
    businessMailingStreet2      : form[FieldNames.commercialMailingStreet2],
    businessMailingZip          : form[FieldNames.commercialMailingZip],
    businessName                : form[FieldNames.commercialBusinessName],
    businessPhone               : form[FieldNames.commercialPhone],
    businessState               : form[FieldNames.commercialState],
    businessStateOfOrganization : form[FieldNames.commercialStateOfOrganization],
    businessStreet              : form[FieldNames.commercialStreet],
    businessStreet2             : form[FieldNames.commercialStreet2],
    businessTIN                 : form[FieldNames.commercialTIN],
    businessWebsite             : form[FieldNames.commercialWebsite],
    businessZip                 : form[FieldNames.commercialZip],
    cashAndEquivalents          : form[FieldNames.commercialBusinessCashAndEquivalents],
    checkingAccountBalance      : form[FieldNames.commercialBusinessCheckingAccountBalance],
    collaterals                 : mapCollateral(form),
    ebita                       : form[FieldNames.commercialBusinessEBITA],
    eidls                       : mapEIDL(form),
    loanAmount                  : form[FieldNames.loanAmount],
    loanTiming                  : form[FieldNames.commercialLoanTiming],
    loanUse                     : form[FieldNames.commercialLoanPurpose],
    netWorth                    : form[FieldNames.commercialBusinessNetWorth],
    owners                      : mapDynamicOwners(form),
    requestedLoanTerm           : form[FieldNames.loanTermDesired],
    totalAssets                 : form[FieldNames.commercialBusinessTotalAssets],
    totalLiabilities            : form[FieldNames.commercialBusinessTotalLiabilities],
    totalSales                  : form[FieldNames.commercialBusinessTotalSales],
  };
};

/**
 * This method maps from the UniversalLoanApplication model back into the backing values that
 * are used on the forms (stored in redux).
 *
 * @param {UniversalLoanApplication} application
 * @returns {Record<string, object>}
*/
export const mapFromUniversalLoanApplication = (application: UniversalLoanApplication, id: number): Record<string, object> => {
  const formValues = {};
  const borrower = application.borrower[0];
  const coBorrower = application.borrower.length > 1 ? application.borrower[1] : undefined;
  const primaryResidence = application.borrower[0].RESIDENCES[0];
  const subjectProperty = application.subjectProperty;
  const reverseMortgage = application.reverseMortgage;

  // universal loan id
  formValues[Fields.universalApplicationId] = id;

  // broker loan officer
  if (application.brokerLoanOfficer) {
    formValues[Fields.brokerLoanOfficerCompanyNMLS] = application.brokerLoanOfficer.companyNMLS;
    formValues[Fields.brokerLoanOfficerEmail] = application.brokerLoanOfficer.email;
    formValues[Fields.brokerLoanOfficerLastName] = application.brokerLoanOfficer.lastName;
    formValues[Fields.brokerLoanOfficerFirstName] = application.brokerLoanOfficer.firstName;
    formValues[Fields.brokerLoanOfficerPhone] = application.brokerLoanOfficer.phone;
  }

  // borrower
  if (borrower) {
    formValues[Fields.borrowerId] = borrower.id;
    formValues[Fields.firstName] = borrower.first_name;
    formValues[Fields.middleName] = borrower.middle_name;
    formValues[Fields.lastName] = borrower.last_name;
    formValues[Fields.suffixName] = borrower.name_suffix;
    formValues[Fields.email] = borrower.email;
    formValues[Fields.phone] = normalizePhone(borrower.phone);
    formValues[Fields.dateOfBirth] = borrower.birth_date;
    formValues[Fields.age] = borrower.age;
  }

  // co-borrower
  formValues[Fields.coBorrowerYN] = coBorrower ? YesOrNo.YES : YesOrNo.NO;
  if (coBorrower) {
    formValues[Fields.coBorrowerId] = coBorrower.id;
    formValues[Fields.coBorrowerFirstName] = coBorrower.first_name;
    formValues[Fields.coBorrowerMiddleName] = coBorrower.middle_name;
    formValues[Fields.coBorrowerLastName] = coBorrower.last_name;
    formValues[Fields.coBorrowerSuffixName] = coBorrower.name_suffix;
    formValues[Fields.coBorrowerEmail] = coBorrower.email;
    formValues[Fields.coBorrowerPhone] = normalizePhone(coBorrower.phone);
    formValues[Fields.coBorrowerDOB] = coBorrower.birth_date;
    formValues[Fields.coBorrowerAge] = coBorrower.age;
  }

  // primary residence
  formValues[Fields.livingPropertyId] = primaryResidence.id;
  formValues[Fields.livingPropertyCity] = primaryResidence.city;
  formValues[Fields.livingPropertyStreet] = primaryResidence.street_address_1;
  formValues[Fields.livingPropertyStreet2] = primaryResidence.street_address_2;
  formValues[Fields.livingPropertyZip] = primaryResidence.postal_code;
  formValues[Fields.livingPropertyState] = primaryResidence.state;

  // reverse mortgage
  formValues[Fields.reverseMortgageLoanPurpose] = reverseMortgage.reverseMortgageLoanPurpose;
  formValues[Fields.quantumLoanId] = reverseMortgage.quantumLoanId;
  formValues[Fields.quantumSimpleId] = reverseMortgage.quantumSimpleId;
  formValues[Fields.isQuantumFileStarted] = reverseMortgage.isQuantumFileStarted ? 1 : 0;

  // if refi
  if (!isUsingReverseToPurchase(formValues)) {
    formValues[Fields.homeValue] = subjectProperty.estimated_amount;

    const mortgage = subjectProperty.liabilitiesAndExpenses?.find(l => l.type === LiabilityAndExpenseType.MORTGAGE);
    formValues[Fields.mortgageBalance] = mortgage?.amount;
    formValues[Fields.mortgageBalanceId] = mortgage?.id;
    formValues[Fields.propertyHasMortgage] = mortgage ? YesOrNo.YES : YesOrNo.NO;

    const hoa = primaryResidence.liabilitiesAndExpenses?.find(l => l.type === LiabilityAndExpenseType.HOA);
    formValues[Fields.livingYearAssociate] = hoa?.amount;
    formValues[Fields.livingYearAssociateId] = hoa?.id;

    const hoi = primaryResidence.liabilitiesAndExpenses?.find(l => l.type === LiabilityAndExpenseType.HOI);
    formValues[Fields.livingYearInsure] = hoi?.amount;
    formValues[Fields.livingYearInsureId] = hoi?.id;

    const propertyTaxes = primaryResidence.liabilitiesAndExpenses?.find(l => l.type === LiabilityAndExpenseType.PROPERTY_TAXES);
    formValues[Fields.livingYearTaxes] = propertyTaxes?.amount;
    formValues[Fields.livingYearTaxesId] = propertyTaxes?.id;
  }

  // if purchase
  if (isUsingReverseToPurchase(formValues)) {
    formValues[Fields.livingOccupancyStart] = primaryResidence.occupancyStartDate;
    formValues[Fields.purchasePrice] = subjectProperty.estimated_amount;
    formValues[Fields.propertyType] = subjectProperty.property_type;
    formValues[Fields.hasSelectedNewHome] = subjectProperty.street_address_1 ? YesOrNo.YES : YesOrNo.NO;
    formValues[Fields.propertyStreet] = subjectProperty.street_address_1;
    formValues[Fields.propertyStreet2] = subjectProperty.street_address_2;
    formValues[Fields.propertyCity] = subjectProperty.city;
    formValues[Fields.propertyState] = subjectProperty.state;
    formValues[Fields.propertyZip] = subjectProperty.postal_code;
  }

  return formValues;
};

const mapAgriculturalLineOfCredit = (form: any, formName: FormType): AgriculturalLineOfCredit => {
  if(formName === FormType.AGRICULTURAL_LINE_OF_CREDIT) {
    return {
      loanAmount  : form[Fields.loanAmount],
      loanPurpose : form[Fields.commercialLoanPurpose],
      loanTiming  : form[Fields.commercialLoanTiming],
    };
  }
  return undefined;
};

const mapAgriculturalPurchaseAcreage = (form: any, formName: FormType): AgriculturalPurchaseAcreage => {
  if(formName === FormType.AGRICULTURAL_PURCHASE_ACREAGE) {
    return {
      annualInsurance       : form[Fields.yearInsure],
      annualTaxes           : form[Fields.yearTaxes],
      downPayment           : form[Fields.downDollar],
      expectedClosingDate   : moment(form[Fields.expectedClosingDate]).format('YYYY-MM-DD'),
      loanAmount            : form[Fields.loanAmount],
      propertyDescription   : form[Fields.commercialDescription],
      propertyIdentity      : form[Fields.agPropertyIdentity],
      propertyType          : form[Fields.agPropertyType],
      propertyValue         : form[Fields.homeValue],
      propertyStreet        : form[Fields.propertyStreet],
      propertyStreet2       : form[Fields.propertyStreet2],
      propertyCity          : form[Fields.propertyCity],
      propertyState         : form[Fields.propertyState],
      propertyZip           : form[Fields.propertyZip],
      ...mapCommercialMortgage(form),
    };
  }
  return undefined;
};

const mapAgriculturalPurchaseEquipment = (form: any, formName: FormType): AgriculturalPurchaseEquipment => {
  if(formName === FormType.AGRICULTURAL_PURCHASE_EQUIPMENT) {
    return {
      description    : form[Fields.commercialDescription],
      downPayment    : form[Fields.downDollar],
      expectedCost   : form[Fields.commercialExpectedCost],
      lifeExpectancy : form[Fields.commercialLifeExpectancy],
      purchaseTiming : form[Fields.commercialLoanTiming],
      type           : form[Fields.agEquipmentType],
    };
  }
  return undefined;
};

const mapAgriculturalRefinance = (form: any, formName: FormType): AgriculturalRefinance => {
  if(formName === FormType.AGRICULTURAL_REFINANCE_PROPERTY) {
    return {
      annualInsurance  : form[Fields.yearInsure],
      annualTaxes      : form[Fields.yearTaxes],
      landSize         : form[Fields.landSize],
      lienBalance      : form[Fields.commercialLienBalance],
      loanAmount       : form[Fields.loanAmount],
      numOfStructures  : form[Fields.numOfStructures],
      refinancePurpose : form[Fields.loanPurpose],
      propertyStreet   : form[Fields.propertyStreet],
      propertyStreet2  : form[Fields.propertyStreet2],
      propertyType     : form[Fields.agPropertyType],
      propertyCity     : form[Fields.propertyCity],
      propertyState    : form[Fields.propertyState],
      propertyValue    : form[Fields.propertyValue],
      propertyZip      : form[Fields.propertyZip],
      ...mapCommercialMortgage(form),
    };
  }
  return undefined;
};
export const handleAgriculturalMapping = (form: any, formName: FormType): Agricultural => {
  const isExistingLoan = form[FieldNames.existingLoanNumber]?.length > 0 || isYes(form[FieldNames.isCommercialExistingCustomer]);
  return {
    business           : mapCommercialBusiness(form),
    employSameBusiness : form[Fields.commercialEmploySameBusiness],
    existingLoanId     : form[FieldNames.existingLoanNumber],
    loanHolder         : form[Fields.commercialLoanHolder],
    loanRenewalDate    : moment(form[Fields.loanRenewalDate]).format('YYYY-MM-DD'),
    owners             : isExistingLoan ? undefined : mapCommercialOwners(form),
    lineOfCredit       : mapAgriculturalLineOfCredit(form, formName),
    purchaseAcreage    : mapAgriculturalPurchaseAcreage(form, formName),
    purchaseEquipment  : mapAgriculturalPurchaseEquipment(form, formName),
    refinance          : mapAgriculturalRefinance(form, formName),
  };
};

export const mapBrokerLoanOfficer = (form: any): BrokerLoanOfficer | undefined => {
  return form[Fields.brokerLoanOfficerLastName] ? {
    companyNMLS : form[Fields.brokerLoanOfficerCompanyNMLS],
    email       : form[Fields.brokerLoanOfficerEmail],
    firstName   : form[Fields.brokerLoanOfficerFirstName],
    lastName    : form[Fields.brokerLoanOfficerLastName],
    phone       : form[Fields.brokerLoanOfficerPhone],
  } : undefined;
};
